import useOfferUrl from 'hooks/Offers/useOfferUrl'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useContext } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import ExperiencesPassportSummaryBox from './ExperiencesPassportSummaryBox'
import useExperiencesPassportForOffer from './useExperiencesPassportForOffer'
import type { ExperiencesPassportOfferSource } from './experiencesPassportTypes'

const FetchSentinel = styled.div`
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`

interface Props {
  offer?: ExperiencesPassportOfferSource
  filters?: App.OfferListFilters
  variant?: 'offer-page' | 'search-tile'
}

function ExperiencesPassportOfferSummary({ offer, filters, variant = 'offer-page' }: Props) {
  const { offer: offerPageOffer, bedbankOffer, checkInDate, checkOutDate } = useContext(OfferPageStateContext)
  const resolvedOffer = offer ?? offerPageOffer ?? bedbankOffer
  // A Property rate can provide a minimal Passport source for a different offer. It is sufficient
  // for eligibility, but does not have the slug required to build an offer-page URL. Keep the CTA
  // pointed at the complete offer represented by the current page instead.
  const offerUrlOffer = offerPageOffer ?? bedbankOffer ?? resolvedOffer
  const resolvedFilters = {
    ...filters,
    checkIn: filters?.checkIn ?? checkInDate,
    checkOut: filters?.checkOut ?? checkOutDate,
  }
  const [fetchRef, inView] = useInView({
    rootMargin: '300px 0px',
    triggerOnce: true,
  })
  const { passport } = useExperiencesPassportForOffer({
    offer: resolvedOffer,
    filters: resolvedFilters,
    shouldFetch: variant !== 'search-tile' || inView,
  })
  const offerUrl = useOfferUrl(offerUrlOffer as App.AnyOffer, {
    filters: resolvedFilters,
    offerLinkIncludesFilters: !!filters || !!resolvedFilters.checkIn || !!resolvedFilters.checkOut,
  })

  if (!passport) {
    return <FetchSentinel ref={fetchRef} aria-hidden="true" />
  }

  return <div ref={fetchRef}>
    <ExperiencesPassportSummaryBox passport={passport} primaryActionTo={offerUrl} variant={variant} />
  </div>
}

export default ExperiencesPassportOfferSummary
